import { useMemo } from 'react';

import { NationalIdentificationNumber, Passport } from '@dreryk/personal-external-identifier-common-api';

import { FieldControllerProps, Pesel, ValidationRules } from '@dreryk/react-common';
import { StyledMaxWidth, TextInput } from '@dreryk/react-components';

import { personalDataTranslationKeys } from '../../../translations';

import { GuardianExternalIdentifierTypeVisitor } from './GuardianExternalIdentifierTypeField';

export type GuardianExternalIdentifierValue = string | null;

type GuardianExternalIdentifierValueFieldProps = FieldControllerProps<
   'guardianExternalIdentifierValue',
   GuardianExternalIdentifierValue
> & {
   readonly guardianExternalIdentifierTypeVisitor: GuardianExternalIdentifierTypeVisitor;
};

export const GuardianExternalIdentifierValueField = (props: GuardianExternalIdentifierValueFieldProps): JSX.Element => {
   const { guardianExternalIdentifierTypeVisitor } = props;

   const validationRules = useMemo(() => {
      return guardianExternalIdentifierTypeVisitor<ValidationRules<{ guardianExternalIdentifierValue: string | null }>>(
         {
            visitPesel: () => ({
               required: true,
               maxLength: Pesel.PESEL_LENGTH,
               validate: (value) =>
                  Pesel.isValid(value as string)
                     ? undefined
                     : personalDataTranslationKeys.identifierValue.validate.invalidPesel,
            }),
            visitPassport: () => ({
               required: true,
               maxLength: Passport.MAX_LENGTH,
               pattern: {
                  value: new RegExp(Passport.PATTERN, 'u'),
                  message: personalDataTranslationKeys.identifierValue.validate.invalidPassportPattern,
               },
               validate: (value) =>
                  Passport.isValueValid(value as string)
                     ? undefined
                     : personalDataTranslationKeys.identifierValue.validate.invalidPassport,
            }),
            visitNationalIdentificationNumber: () => ({
               required: true,
               maxLength: NationalIdentificationNumber.MAX_LENGTH,
               pattern: {
                  value: new RegExp(NationalIdentificationNumber.PATTERN, 'u'),
                  message:
                     personalDataTranslationKeys.identifierValue.validate.invalidNationalIdentificationNumberPattern,
               },
               validate: (value) =>
                  NationalIdentificationNumber.isValueValid(value as string)
                     ? undefined
                     : personalDataTranslationKeys.identifierValue.validate.invalidNationalIdentificationNumber,
            }),
         }
      );
   }, [guardianExternalIdentifierTypeVisitor]);

   return (
      <StyledMaxWidth maxWidth={263}>
         <TextInput
            testId={'guardianExternalIdentifierValue'}
            label={personalDataTranslationKeys.identifierValue.label}
            placeholder={guardianExternalIdentifierTypeVisitor(
               (type) => personalDataTranslationKeys.identifierValue.placeholder[type]
            )}
            controller={props.register('guardianExternalIdentifierValue', validationRules)}
            defaultValue={null}
         />
      </StyledMaxWidth>
   );
};
